/**
 * ArtifactPreviewCard — Feb 2026 · Iter88
 *
 * Renders the "body" of a Stage 9 artefact preview (image / boundary
 * SVG / metadata table). Shared between:
 *   • ArtifactPreviewPopover  — hover-to-peek
 *   • PinnedPreviewPanel      — persistent right-side compare panel
 *
 * Keeping the render tree in one place ensures the two entry points
 * always agree on layout & polish.
 */
import { MapTrifold, ImageSquare, Files } from "@phosphor-icons/react";

function BoundarySVG({ geojson, bbox, height = 96 }) {
  const coords = geojson?.coordinates?.[0];
  if (!coords || !coords.length || !bbox) return null;
  const [minX, minY, maxX, maxY] = bbox;
  const w = maxX - minX || 1;
  const h = maxY - minY || 1;
  const cw = 200, ch = height, pad = 8;
  const sx = (cw - 2 * pad) / w;
  const sy = (ch - 2 * pad) / h;
  const s = Math.min(sx, sy);
  const ox = pad + ((cw - 2 * pad) - w * s) / 2;
  const oy = pad + ((ch - 2 * pad) - h * s) / 2;
  const pts = coords
    .map(([x, y]) => `${(ox + (x - minX) * s).toFixed(1)},${(ch - oy - (y - minY) * s).toFixed(1)}`)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${cw} ${ch}`} className="w-full rounded-md bg-slate-100"
             style={{ height: `${height}px` }}>
      <polygon points={pts} fill="rgba(29,78,216,0.18)" stroke="rgb(29,78,216)" strokeWidth="1.2" />
    </svg>
  );
}

export default function ArtifactPreviewCard({ preview, compact = false }) {
  if (!preview) return null;
  const iconSize = compact ? 12 : 14;
  return (
    <div className="space-y-2" data-testid="artifact-preview-card">
      <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
        {preview.kind === "image" && <ImageSquare size={iconSize} className="text-brand-blue" weight="duotone" />}
        {preview.kind === "boundary" && <MapTrifold size={iconSize} className="text-brand-blue" weight="duotone" />}
        {preview.kind === "metadata" && <Files size={iconSize} className="text-brand-blue" weight="duotone" />}
        <div className="text-[11.5px] font-semibold text-slate-800 truncate flex-1"
              data-testid="artifact-preview-title">
          {preview.title || "Preview"}
        </div>
      </div>

      {preview.kind === "image" && preview.image_url && (
        <img
          src={preview.image_url} alt={preview.title || "preview"}
          data-testid="artifact-preview-image"
          className={`w-full ${compact ? "h-32" : "h-48"} object-contain rounded-md bg-slate-50`}
          loading="lazy"
        />
      )}
      {preview.kind === "boundary" && (
        <>
          <BoundarySVG geojson={preview.geojson} bbox={preview.bbox} height={compact ? 96 : 160} />
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10.5px] text-slate-600 pt-1">
            {preview.area_km2 != null && (<><span>Area</span><span className="font-mono text-right">{Number(preview.area_km2).toFixed(3)} km²</span></>)}
            {preview.perimeter_km != null && (<><span>Perimeter</span><span className="font-mono text-right">{Number(preview.perimeter_km).toFixed(3)} km</span></>)}
            {preview.source_crs && (<><span>CRS</span><span className="font-mono text-right">{preview.source_crs}</span></>)}
            {preview.utm_epsg && (<><span>UTM</span><span className="font-mono text-right">EPSG:{preview.utm_epsg}</span></>)}
            {preview.centroid && (<><span>Centroid</span><span className="font-mono text-right">{preview.centroid[1].toFixed(3)}, {preview.centroid[0].toFixed(3)}</span></>)}
          </div>
        </>
      )}
      {preview.kind === "metadata" && (
        <div className="grid grid-cols-3 gap-x-2 gap-y-0.5 text-[10.5px] text-slate-600 pt-1">
          {preview.filename && (<><span className="col-span-1">File</span><span className="col-span-2 font-mono text-right truncate">{preview.filename}</span></>)}
          {preview.size_kb != null && (<><span className="col-span-1">Size</span><span className="col-span-2 font-mono text-right">{preview.size_kb} KB</span></>)}
          {preview.sha256 && (<><span className="col-span-1">SHA-256</span><span className="col-span-2 font-mono text-right truncate">{preview.sha256.slice(0, 12)}…</span></>)}
          {preview.timestamp && (<><span className="col-span-1">Created</span><span className="col-span-2 font-mono text-right">{new Date(preview.timestamp).toLocaleString()}</span></>)}
          {preview.description && (<span className="col-span-3 text-slate-500 pt-1">{preview.description}</span>)}
        </div>
      )}
    </div>
  );
}
